import {useQuery} from '@tanstack/react-query';
import * as I from 'immutable';
import React from 'react';
import {Helmet} from 'react-helmet';
import {Navigate} from 'react-router-dom';
import {NumberParam, useQueryParam} from 'use-query-params';

import ErrorPage from 'app/components/Error/ErrorPage';
import Loading from 'app/components/Loading';
import {ShowPushNotification} from 'app/components/Notification';
import StoryView from 'app/components/StoryView';
import {useStorySlides} from 'app/components/StoryView/storySlides';
import {api} from 'app/modules/Remote';
import {ApiFeature, ApiNote, ApiReport, ReportState} from 'app/modules/Remote/Feature';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization} from 'app/modules/Remote/Organization';
import {ApiProject} from 'app/modules/Remote/Project';
import {
  makeDefaultOverlayVisibility,
  makeOverlaySettings,
} from 'app/pages/MonitorProjectView/utils';
import {FeatureDataProvider} from 'app/providers/FeatureDataProvider';
import {useFeaturesState, useGeoJsonFeaturesLoader} from 'app/providers/FeaturesProvider';
import {WithProject} from 'app/providers/ProjectsProvider';
import {StateApiNote, hydrateApiNote, noteDisplayName} from 'app/stores/NotesStore';
import {recordEvent} from 'app/tools/Analytics';
import {HideHubspotWidget} from 'app/tools/Hubspot';
import {useContinuity} from 'app/utils/hookUtils';
import * as layerUtils from 'app/utils/layerUtils';
import {getMultiFeaturePropertyParts} from 'app/utils/multiFeaturePropertyUtils';
import * as routeUtils from 'app/utils/routeUtils';

const EMPTY_NOTES: StateApiNote[] = [];
// The story has no org roster; bylines resolve through the name the API recorded.
const NO_ORG_NAMES = new Map<string, string>();

interface Props {
  organization: I.ImmutableOf<ApiOrganization>;
  firebaseToken: string;
  projectIdParam: string;
  /** A feature id, lens id, or lens id prefix, as in the project dashboard URL. */
  featureIdParam: string;
  reportId: number;
}

/**
 * A saved report, presented as a map slideshow. Resolves the project and its
 * primary feature collection, then hands off to StoryLoader for everything
 * that needs a FeatureDataProvider.
 */
const StoryViewPage: React.FunctionComponent<Props> = ({
  organization,
  firebaseToken,
  projectIdParam,
  featureIdParam,
  reportId,
}) => {
  const orgIdPrefix = routeUtils.makeUuidPrefix(organization.get('id'));

  return (
    <WithProject projectIdParam={projectIdParam} renderLoading={() => <Loading />}>
      {(project, projectFeatureCollections, _actions, {error}) => {
        const featureCollection = projectFeatureCollections.get('primary', I.List()).first();

        if (!project || error || !featureCollection) {
          return (
            <>
              <ShowPushNotification message="There was a problem loading that portfolio. Ensure you are signed in to the correct organization." />
              <Navigate to={`/${orgIdPrefix}/projects`} replace />
            </>
          );
        }

        return (
          <FeatureDataProvider featureCollectionId={featureCollection.get('id')}>
            <HideHubspotWidget />
            <StoryLoader
              organization={organization}
              firebaseToken={firebaseToken}
              featureIdParam={featureIdParam}
              reportId={reportId}
              project={project}
              featureCollection={featureCollection}
              overlayFeatureCollections={projectFeatureCollections.get('overlay', I.List())}
            />
          </FeatureDataProvider>
        );
      }}
    </WithProject>
  );
};

const StoryLoader: React.FunctionComponent<{
  organization: I.ImmutableOf<ApiOrganization>;
  firebaseToken: string;
  featureIdParam: string;
  reportId: number;
  project: I.ImmutableOf<ApiProject>;
  featureCollection: I.ImmutableOf<ApiFeatureCollection>;
  overlayFeatureCollections: I.ImmutableOf<ApiFeatureCollection[]>;
}> = ({
  organization,
  firebaseToken,
  featureIdParam,
  reportId,
  project,
  featureCollection,
  overlayFeatureCollections,
}) => {
  const projectId = project.get('id');
  const featureCollectionId = featureCollection.get('id');

  const [features, featuresStatus, featuresActions] = useFeaturesState({featureCollection});

  // The report's own property. A tiled collection pauses after its first page
  // (status 'ready'), so once the list stops fetching, fall back to the one
  // feature by id.
  const listedFeature = React.useMemo(
    () => features.find((f) => routeUtils.featureMatchesParam(f!, featureIdParam)) ?? null,
    [features, featureIdParam]
  );
  const fetchedFeature =
    !listedFeature && featuresStatus !== 'fetching' && /^\d+$/.test(featureIdParam)
      ? featuresActions.getFeature(parseInt(featureIdParam, 10))
      : null;
  const anchorFeature: I.ImmutableOf<ApiFeature> | null =
    listedFeature ?? (fetchedFeature?.status === 'some' ? fetchedFeature.value : null);
  const anchorFeatureId = anchorFeature?.get('id') ?? null;

  const reportQuery = useQuery({
    queryKey: ['story-report', anchorFeatureId, reportId],
    queryFn: async () => {
      const response = await api.reports(anchorFeatureId!).get(reportId);
      return response.get('data').toJS() as ApiReport<ReportState>;
    },
    enabled: anchorFeatureId !== null,
  });
  const report = reportQuery.data ?? null;

  // Once per report opened. Depending on these primitives rather than the report
  // object means a refetch of the same report doesn't count as another view.
  const loadedReportId = report?.id;
  const isMultilocation = report?.isMultilocation ?? false;
  const notePageCount = report?.data.notePages.length ?? 0;
  React.useEffect(() => {
    if (loadedReportId !== undefined) {
      recordEvent('Viewed report as story', {
        reportId: loadedReportId,
        isMultilocation,
        notePageCount,
      });
    }
  }, [loadedReportId, isMultilocation, notePageCount]);

  // Every part of the property. Overview pages index into this list, and it's
  // what the map outlines, whether or not the report spans all of them. Held
  // by value so the features list streaming in doesn't rebuild every slide.
  const propertyParts = useContinuity(
    React.useMemo<I.ImmutableOf<ApiFeature[]>>(() => {
      if (!anchorFeature) {
        return I.List();
      }
      // A feature fetched by id isn't in the list, so it has to be added before
      // grouping by property name or it would be missing from its own story.
      const candidates = features.includes(anchorFeature) ? features : features.push(anchorFeature);
      return I.List(getMultiFeaturePropertyParts(candidates, anchorFeature).map((p) => p.feature));
    }, [features, anchorFeature]),
    I.is
  );

  // A report only carries notes from other parts when it was saved as a
  // multi-location report.
  const noteFeatureIds = React.useMemo(() => {
    if (!anchorFeatureId) {
      return [];
    }
    return report?.isMultilocation
      ? propertyParts.map((f) => f!.get('id') as number).toArray()
      : [anchorFeatureId];
  }, [report?.isMultilocation, propertyParts, anchorFeatureId]);

  const notesQuery = useQuery({
    queryKey: ['story-notes', projectId, featureCollectionId, noteFeatureIds],
    queryFn: async () => {
      const notesApi = api.featureCollections.features(featureCollectionId);
      const responses = await Promise.all(
        noteFeatureIds.map((featureId) =>
          notesApi.listNotes(projectId, featureId, {perPage: 100}, {getAllPages: 2})
        )
      );
      return responses
        .flatMap((response) => response.get('data').toJS() as ApiNote[])
        .map((note) =>
          hydrateApiNote(note, {name: noteDisplayName(note, NO_ORG_NAMES), isAuthor: false})
        );
    },
    enabled: report !== null && noteFeatureIds.length > 0,
  });
  const notes = notesQuery.data ?? EMPTY_NOTES;

  const enrolledLayerKeys = React.useMemo(
    () => layerUtils.getEnrolledLayerKeysForFeatureCollection(featureCollection),
    [featureCollection]
  );
  const {slides, skippedCount} = useStorySlides({
    report,
    notes,
    anchorFeature,
    propertyParts,
    organizationName: organization.get('name'),
    enrolledLayerKeys,
  });

  const [slideParam, setSlideParam] = useQueryParam(
    routeUtils.STORY_SLIDE_QUERY_PARAM,
    NumberParam
  );
  // A hand-edited param can be NaN or fractional; anything but a whole number reads as slide 1.
  const requestedSlide = Number.isInteger(slideParam) ? (slideParam as number) : 1;
  const slideIndex = Math.min(Math.max(requestedSlide - 1, 0), Math.max(slides.length - 1, 0));
  const onChangeSlide = React.useCallback(
    (index: number) => setSlideParam(index + 1, 'replaceIn'),
    [setSlideParam]
  );

  const geoJsonFeaturesLoader = useGeoJsonFeaturesLoader();
  const overlaySettings = React.useMemo(
    () => makeOverlaySettings(featureCollection, overlayFeatureCollections),
    [featureCollection, overlayFeatureCollections]
  );
  const defaultOverlayVisibility = React.useMemo(
    () => makeDefaultOverlayVisibility(featureCollection, overlayFeatureCollections),
    [featureCollection, overlayFeatureCollections]
  );

  if (!anchorFeature) {
    if (featuresStatus !== 'fetching' && fetchedFeature?.status !== 'unknown') {
      return <ErrorPage message="We couldn't find that property." linkTo={null} />;
    }
    return <Loading />;
  }

  if (reportQuery.isError || notesQuery.isError) {
    return <ErrorPage message="We couldn't load that report." linkTo={null} />;
  }

  if (!report || notesQuery.isPending) {
    return <Loading />;
  }

  return (
    <>
      <Helmet>
        <title>{report.title} | Lens</title>
      </Helmet>
      <StoryView
        title={report.title}
        backUrl={routeUtils.makeProjectDashboardUrl(organization, project, 'map', [anchorFeature])}
        slides={slides}
        skippedCount={skippedCount}
        slideIndex={slideIndex}
        onChangeSlide={onChangeSlide}
        isMultilocation={report.isMultilocation}
        features={propertyParts}
        featureCollection={featureCollection}
        overlayFeatureCollections={overlayFeatureCollections}
        overlaySettings={overlaySettings}
        defaultOverlayVisibility={defaultOverlayVisibility}
        geoJsonFeaturesLoader={geoJsonFeaturesLoader}
        organization={organization}
        firebaseToken={firebaseToken}
      />
    </>
  );
};

export default StoryViewPage;
